/**
 * Generic error boundary, backing SHELL-005 (boot) and SHELL-006 (route).
 *
 * Preact routes a synchronous throw during render, and one thrown inside an
 * effect callback, to the nearest ancestor component that defines
 * `componentDidCatch` -- effects included, because Preact's hooks runtime
 * wraps effect invocation in its own try/catch and reports through the same
 * `options._catchError` path render errors use. That is what lets a throw
 * from `game.mount()`, called from an effect rather than from render, land
 * here rather than crashing past it. See the "one decision left open" note in
 * docs/prompts/sprint-1b.md for the concrete case (`goal.ts`).
 *
 * `fallback` is a render prop rather than fixed markup so SHELL-005 (a
 * reload button, nothing else assumed to exist) and SHELL-006 (a route back
 * to the picker, everything else in the shell still standing) can use the
 * same mechanism with different fallback screens.
 */

import { Component } from 'preact';
import type { ComponentChildren } from 'preact';

interface ErrorBoundaryProps {
  fallback: (error: Error) => ComponentChildren;
  children?: ComponentChildren;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  override componentDidCatch(error: unknown): void {
    const asError = error instanceof Error ? error : new Error(String(error));
    // DATA-049's reasoning applies here too: a refusal nobody can see in the
    // console is a refusal nobody can diagnose.
    console.error('[ErrorBoundary]', asError);
    this.setState({ error: asError });
  }

  override render(): ComponentChildren {
    const { error } = this.state;
    return error ? this.props.fallback(error) : this.props.children;
  }
}
